// Imports
import Vue from 'vue'
import Router from 'vue-router'
import i18n from '@/plugins/i18n-instance'
import { reportVisit } from '@/utils/analytics'
import { schedulePrerenderReady } from '@/utils/prerender'

Vue.use(Router)

const fallbackLangs = ['en', 'zh', 'es', 'ar', 'pt', 'fr', 'de', 'ko']
let supportedLangs = [...fallbackLangs]
// 默认语言由构建时环境变量 VUE_APP_DEFAULT_LANG 决定（国内站为 zh，海外站为 en）
const envDefaultLang = (process.env.VUE_APP_DEFAULT_LANG || 'en').toLowerCase()
const defaultLang = fallbackLangs.includes(envDefaultLang) ? envDefaultLang : 'en'
const langPattern = '[a-z]{2}'

function upsertHeadMeta(name, content, managedBy) {
  if (typeof document === 'undefined') return
  let meta = document.head.querySelector(`meta[name="${name}"]`)
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('name', name)
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', content)
  meta.dataset.managedBy = managedBy
}

function removeManagedHeadMeta(name, managedBy) {
  if (typeof document === 'undefined') return
  const meta = document.head.querySelector(`meta[name="${name}"]`)
  if (meta?.dataset?.managedBy === managedBy) {
    meta.remove()
  }
}

function syncNotFoundHeadMeta(routeName) {
  if (routeName === 'FourOhFour') {
    upsertHeadMeta('robots', 'noindex, nofollow', 'yooxplore-router')
    upsertHeadMeta('render:status_code', '404', 'yooxplore-router')
    return
  }

  removeManagedHeadMeta('robots', 'yooxplore-router')
  removeManagedHeadMeta('render:status_code', 'yooxplore-router')
}

function scheduleNotFoundHeadMetaSync(routeName) {
  syncNotFoundHeadMeta(routeName)

  if (typeof window === 'undefined') return

  Vue.nextTick(() => {
    syncNotFoundHeadMeta(routeName)

    window.setTimeout(() => {
      syncNotFoundHeadMeta(routeName)
    }, 0)

    window.setTimeout(() => {
      syncNotFoundHeadMeta(routeName)
    }, 120)
  })
}

function loadSupportedLangsFromCache() {
  try {
    const raw = localStorage.getItem('sys_language_codes')
    const parsed = raw ? JSON.parse(raw) : []
    if (Array.isArray(parsed) && parsed.length > 0) {
      supportedLangs = parsed
    }
  } catch (error) {
    supportedLangs = [...fallbackLangs]
  }
}

const homeChildren = [
  {
    path: '',
    name: 'Home',
    component: () => import('@/views/home/Index.vue'),
  },
  {
    path: 'home',
    name: 'HomeAlias',
    component: () => import('@/views/home/Index.vue'),
  },
  {
    path: 'about',
    name: 'About',
    component: () => import('@/views/about/Index.vue'),
    meta: {
      src: require('@/assets/about.jpg'),
      i18n: 'about.title',
    },
  },
  {
    path: 'partnership',
    name: 'Partnership',
    component: () => import('@/views/partnership/Index.vue'),
    meta: {
      src: require('@/assets/marketing.jpg'),
      i18n: 'partnership.title',
    },
  },
  {
    path: 'blog',
    name: 'Blog',
    component: () => import('@/views/blog/Index.vue'),
    meta: {
      src: require('@/assets/news.jpg'),
      i18n: 'blog.title',
    },
  },
  {
    path: 'blog/category/:slug',
    name: 'BlogCategory',
    component: () => import('@/views/blog/Index.vue'),
    meta: {
      src: require('@/assets/news.jpg'),
      i18n: 'blog.title',
    },
  },
  {
    path: 'blog/tag/:slug',
    name: 'BlogTag',
    component: () => import('@/views/blog/Index.vue'),
    meta: {
      src: require('@/assets/news.jpg'),
      i18n: 'blog.title',
    },
  },
  {
    path: 'blog/:slug',
    name: 'BlogArticle',
    component: () => import('@/views/news/Article.vue'),
    meta: { src: require('@/assets/article.jpg') },
  },
  {
    path: 'products',
    name: 'Products',
    component: () => import('@/views/products/Index.vue'),
    meta: {
      src: require('@/assets/about.jpg'),
      i18n: 'products.title',
    },
  },
  {
    path: 'products/:itemId',
    name: 'ProductsItem',
    component: () => import('@/views/products/ProductsItem.vue'),
    meta: { src: require('@/assets/project.jpg') },
  },
  {
    path: 'solutions',
    name: 'Solutions',
    component: () => import('@/views/solutions/Index.vue'),
    meta: {
      src: require('@/assets/about.jpg'),
      i18n: 'solutions.title',
    },
  },
  {
    path: 'solutions/:itemId',
    name: 'SolutionsItem',
    component: () => import('@/views/solutions/SolutionsItem.vue'),
    meta: { src: require('@/assets/project.jpg') },
  },
  {
    path: 'news/:slug',
    name: 'Article',
    component: () => import('@/views/news/Article.vue'),
    meta: { src: require('@/assets/article.jpg') },
  },
  {
    path: 'contact',
    name: 'Contact',
    component: () => import('@/views/contact-us/Index.vue'),
    meta: {
      src: require('@/assets/contact.jpg'),
      i18n: 'contact.title',
    },
  },
  {
    path: '*',
    name: 'FourOhFour',
    component: () => import('@/views/404/Index.vue'),
  },
]

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior: (to, from, savedPosition) => {
    if (to.hash) return { selector: to.hash }
    if (savedPosition) return savedPosition

    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/login/Index.vue'),
    },
    {
      path: `/:lang(${langPattern})`,
      component: () => import('@/layouts/home/Index.vue'),
      children: homeChildren,
    },
    {
      path: '/',
      redirect: `/${defaultLang}`,
    },
    {
      path: '*',
      redirect: `/${defaultLang}`,
    },
  ],
})

router.beforeEach((to, from, next) => {
  loadSupportedLangsFromCache()
  if (to.path.startsWith('/login')) {
    return next()
  }
  const segments = to.path.split('/').filter(Boolean)
  const maybeLang = segments[0]
  if (!supportedLangs.includes(maybeLang)) {
    const lang = supportedLangs.includes(i18n.locale) ? i18n.locale : (supportedLangs[0] || defaultLang)
    const target = `/${lang}${to.path === '/' ? '' : to.path}`
    return next(target)
  }
  if (i18n.locale !== maybeLang) {
    i18n.locale = maybeLang
  }
  return next()
})

// =============================================
// 路由后置守卫：GA4 页面浏览上报 + 自建后端访问记录上报
// - GA4：触发 page_view 事件（需在 index.html 中加载 gtag.js，且 .env 中配置 VUE_APP_GA_MEASUREMENT_ID）
// - 自建后端：调用 reportVisit 将访问数据 POST 到 /api/public/visit，由后端记录 IP、地域等
// =============================================
router.afterEach((to) => {
  scheduleNotFoundHeadMetaSync(to.name)
  schedulePrerenderReady(1500)

  const pagePath = to.fullPath // 当前页面完整路径（含查询参数等）
  const pageTitle = document.title // 页面标题
  const lang = i18n.locale || '' // 当前语言代码

  // GA4 手动上报页面浏览（因为 gtag config 中设置了 send_page_view: false）
  if (typeof window.gtag === 'function' && process.env.VUE_APP_GA_MEASUREMENT_ID) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle,
      page_location: window.location.href,
    })
  }

  // 自建后端上报（异步，不阻塞页面加载）
  reportVisit(pagePath, pageTitle, lang)
})

export default router
